import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from 'shared-lib';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from './App';
import './index.css';
import './firebase';

// Create a theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#00AB9B',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#101D3B',
    },
    text: {
      primary: '#101D3B',
      secondary: 'rgba(16, 29, 59, 0.6)',
    },
    background: {
      default: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    h1: {
      fontWeight: 700,
      color: '#101D3B',
    },
    h2: {
      fontWeight: 700,
      color: '#101D3B',
    },
    h3: {
      fontWeight: 700,
      color: '#101D3B',
    },
    h4: {
      fontWeight: 700,
      color: '#101D3B',
    },
    h5: {
      fontWeight: 700,
      color: '#101D3B',
    },
    h6: {
      fontWeight: 700,
      color: '#101D3B',
      fontSize: '18px',
    },
    body1: {
      fontSize: '14px',
      color: '#101D3B',
    },
    body2: {
      fontSize: '14px',
      color: 'rgba(16, 29, 59, 0.6)',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 5,
          padding: '10px 20px',
          transition: 'background-color 0.3s ease',
        },
        contained: {
          backgroundColor: '#00AB9B',
          '&:hover': {
            backgroundColor: '#00AB9B',
            filter: 'brightness(0.9)',
          },
        },
        outlined: {
          borderColor: '#00AB9B',
          color: '#00AB9B',
          '&:hover': {
            borderColor: '#00AB9B',
            backgroundColor: 'rgba(0, 171, 155, 0.04)',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        InputLabelProps: {
          shrink: true,
        },
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 16,
            '& fieldset': {
              borderColor: 'rgba(16, 29, 59, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(16, 29, 59, 0.3)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00AB9B',
            },
          },
          '& .MuiInputLabel-outlined': {
            backgroundColor: '#FFFFFF',
            paddingLeft: '4px',
            paddingRight: '4px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid rgba(16, 29, 59, 0.2)',
          boxShadow: '0px 8px 32px -4px rgba(16, 29, 59, 0.1)',
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          subtitle1: 'h6',
          subtitle2: 'h6',
          body1: 'p',
          body2: 'p',
        },
      },
      styleOverrides: {
        root: {
          color: '#101D3B',
        },
      },
    },
  },
});

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60000, // 1 minute
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <App />
          </Router>
        </AuthProvider>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>
); 